// --------------------------------------------------------------
// Created On: 2026-10-27
// Author: Zachary Thomas
//
// Last Modified: 2026-10-30
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Active, DndContext, Over } from "@dnd-kit/core";
import Modal from "src/components/Modal/Modal";
import ModalHeader from "src/components/ModalHeader/ModalHeader";
import ModalBody from "src/components/ModalBody/ModalBody";
import ModalFooter from "src/components/ModalFooter/ModalFooter";
import Error from "src/components/Error/Error";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import ConfirmModal from "src/components/ConfirmModal/ConfirmModal";
import DroppableArea from "src/components/DroppableArea/DroppableArea";
import HighlightingRuleForm from "src/components/HighlightingRuleForm/HighlightingRuleForm";
import { MAX_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES, VIEW_INITIAL_HIGHLIGHT_COLOR } from "src/constants/miscellaneous";
import deepCopy from "src/utilities/deepCopy";
import enforceHighlightingRuleValueType from "src/utilities/enforceHighlightingRuleValueType";
import styles from "./AttributeHighlightModal.module.scss";

// Modal for setting up the highlight colors of a system control attribute.
export default function AttributeHighlightModal(props: Props): Component {
  const [rules, setRules] = useState<FormRule[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showConfirmExit, setShowConfirmExit] = useState<boolean>(false);
  const enumerationOptions = useMemo(
    () => getEnumerationOptions(props.regEnumId, props.enumerationMap),
    [props.regEnumId, JSON.stringify(props.enumerationMap)]
  );

  // Each time the modal is opened, start over from the rules that are currently set on the attribute.
  useEffect(() => {
    if (props.showModal) {
      const sortedRules = [...props.highlightingRules].sort((a, b) => a.sortPriority - b.sortPriority);
      setRules(
        sortedRules.map((rule, i) => ({
          formId: i + 1,
          comparator: rule.comparator,
          valueType: rule.valueType,
          value: rule.value,
          colorHexCode: rule.colorHexCode,
        }))
      );
      setErrorMessage("");
      setShowConfirmExit(false);
    }
  }, [props.showModal]);

  // Returns the options for an enumeration attribute.
  function getEnumerationOptions(regEnumId: number | null, enumerationMap: EnumerationMap): EnumerationOption[] {
    const enumerationOptions: EnumerationOption[] = [];
    if (regEnumId !== null && enumerationMap[regEnumId] !== undefined) {
      Object.keys(enumerationMap[regEnumId]).forEach((index) => {
        enumerationOptions.push({ index: parseInt(index, 10), name: enumerationMap[regEnumId][index] });
      });
    }
    return enumerationOptions;
  }

  // Returns whether the rules differ from the rules that were set when the modal was opened.
  function hasChanges(): boolean {
    const currentRules = rules.map((rule) => ({
      comparator: rule.comparator,
      valueType: rule.valueType,
      value: rule.value,
      colorHexCode: rule.colorHexCode,
    }));
    const previousRules = [...props.highlightingRules]
      .sort((a, b) => a.sortPriority - b.sortPriority)
      .map((rule) => ({
        comparator: rule.comparator,
        valueType: rule.valueType,
        value: rule.value,
        colorHexCode: rule.colorHexCode,
      }));
    return JSON.stringify(currentRules) !== JSON.stringify(previousRules);
  }

  // Exit the modal if no changes have been made. Otherwise prompt the user.
  function exitModal(): void {
    if (hasChanges()) {
      setShowConfirmExit(true);
    } else {
      discardChanges();
    }
  }

  // Exit without saving changes.
  function discardChanges(): void {
    setShowConfirmExit(false);
    setErrorMessage("");
    props.onClose();
  }

  // Check if all of the rules are valid, and set an error message if one is not.
  function rulesAreValid(): boolean {
    for (const [i, rule] of rules.entries()) {
      if (rule.valueType === "NUMBER") {
        if (rule.comparator === "<>") {
          const values = rule.value.split(",");
          const primaryValue = parseFloat(values[0]);
          const secondaryValue = parseFloat(values[1]);
          if (isNaN(primaryValue) || isNaN(secondaryValue)) {
            setErrorMessage(
              `Highlighting rule #${
                i + 1
              } is invalid. When highlighting between two numbers, both numbers must be valid.`
            );
            return false;
          } else if (primaryValue >= secondaryValue) {
            setErrorMessage(
              `Highlighting rule #${i + 1} is invalid. When highlighting between two numbers, ` +
                `the first number must be smaller than the second number.`
            );
            return false;
          }
        } else if (isNaN(parseFloat(rule.value))) {
          setErrorMessage(`Highlighting rule #${i + 1} has an invalid value.`);
          return false;
        }
      }
    }
    return true;
  }

  // Save the rules to the attribute.
  function saveChanges(): void {
    setShowConfirmExit(false);
    if (rulesAreValid()) {
      setErrorMessage("");
      props.onSave(
        rules.map((rule, i) => ({
          comparator: rule.comparator,
          valueType: rule.valueType,
          value: rule.value,
          colorHexCode: rule.colorHexCode,
          sortPriority: i + 1,
        }))
      );
    }
  }

  // Add a highlighting rule.
  function addRule(): void {
    let formId = 1;
    rules.forEach((rule) => {
      if (rule.formId >= formId) {
        formId = rule.formId + 1;
      }
    });
    setRules([
      ...rules,
      { formId: formId, comparator: "=", valueType: "NUMBER", value: "0", colorHexCode: VIEW_INITIAL_HIGHLIGHT_COLOR },
    ]);
  }

  // Update a highlighting rule.
  function updateRule(
    formId: number,
    comparator: string,
    valueType: string,
    value: string,
    colorHexCode: string
  ): void {
    const rulesDeepCopy = deepCopy(rules);
    const index = rulesDeepCopy.findIndex((rule) => rule.formId === formId);
    if (index >= 0) {
      const updatedRule: FormRule = { formId: formId, comparator, valueType, value, colorHexCode };
      // Make sure that the settings are valid for the value type, such as removing unused secondary values.
      enforceHighlightingRuleValueType(value, valueType, comparator, updatedRule);
      rulesDeepCopy.splice(index, 1, updatedRule);
      setRules(rulesDeepCopy);
    }
  }

  // Delete a highlighting rule.
  function deleteRule(formId: number): void {
    setRules(rules.filter((rule) => rule.formId !== formId));
  }

  // Handles changing the order of the rules when a rule is dragged to a new position.
  function handleDragEnd(over: Over | null, active: Active): void {
    if (over !== null) {
      const rulesDeepCopy = deepCopy(rules);
      const selectedRuleIndex = rulesDeepCopy.findIndex((rule) => rule.formId === active.id);
      if (selectedRuleIndex > -1) {
        // Start by removing the rule from the list before adding it to its new location.
        const [selectedRule] = rulesDeepCopy.splice(selectedRuleIndex, 1);
        if (over.id === 0) {
          setRules([selectedRule, ...rulesDeepCopy]);
        } else {
          const previousRuleIndex = rulesDeepCopy.findIndex((rule) => rule.formId === over.id);
          if (previousRuleIndex > -1) {
            rulesDeepCopy.splice(previousRuleIndex + 1, 0, selectedRule);
            setRules(rulesDeepCopy);
          }
        }
      }
    }
  }

  return (
    <div data-test="attribute-highlight-modal">
      <Modal
        show={props.showModal}
        onHide={() => exitModal()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="xl"
        animation
        centered
      >
        <ModalHeader>
          <h5>Highlight Colors{props.attributeName.length > 0 ? ` - ${props.attributeName}` : ""}</h5>
        </ModalHeader>

        <ModalBody>
          <div className="mx-2 mt-2">
            <div className="mx-1">
              <label className={styles.title}>Highlighting Rules</label>{" "}
              <IconTooltip
                id="attribute-highlight-priority-tooltip"
                icon="info-circle"
                message="If a value would be highlighted by two separate rules, the rule higher in the list will take priority."
                color="var(--info-tooltip)"
              />
              <button
                data-test="attribute-highlight-add-rule-button"
                type="button"
                className={`${styles.addButton} btn btn-success float-end`}
                disabled={!props.isHighlightable || rules.length >= MAX_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES}
                onClick={() => addRule()}
              >
                <span className="d-none d-sm-inline">Add Rule</span>
                <i className="d-inline d-sm-none fa fa-fw fa-plus fa-xs" />
              </button>
            </div>

            {!props.isHighlightable && (
              <p className={`${styles.note} mx-1 mt-2`}>This attribute does not support highlighting rules.</p>
            )}

            {rules.length > 0 && (
              <DndContext onDragEnd={(e) => handleDragEnd(e.over, e.active)}>
                <DroppableArea id={0} />
                {rules.map((rule) => (
                  <Fragment key={rule.formId}>
                    <HighlightingRuleForm
                      formId={rule.formId}
                      comparator={rule.comparator as ViewComparator}
                      valueType={rule.valueType as ViewValueType}
                      value={rule.value}
                      colorHexCode={rule.colorHexCode}
                      units={props.units}
                      isBoolean={props.isBoolean}
                      isEnumeration={props.regEnumId !== null}
                      enumerationOptions={enumerationOptions}
                      hideValueTypeSelector
                      onChange={(comparator, valueType, value, colorHexCode) =>
                        updateRule(rule.formId, comparator, valueType, value, colorHexCode)
                      }
                      onDelete={() => deleteRule(rule.formId)}
                    />
                    <DroppableArea id={rule.formId} />
                  </Fragment>
                ))}
              </DndContext>
            )}

            {errorMessage.length > 0 && (
              <div className="mt-4">
                <Error message={errorMessage} />
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter className={styles.footer}>
          <button
            data-test="attribute-highlight-save-button"
            type="button"
            className="btn btn-primary"
            onClick={() => saveChanges()}
          >
            Done
          </button>
          <button
            data-test="attribute-highlight-cancel-button"
            type="button"
            className="btn btn-secondary"
            onClick={() => exitModal()}
          >
            Cancel
          </button>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        showModal={showConfirmExit}
        title="Changes will be discarded!"
        content="Are you sure that you want to discard your changes?"
        yesText="Discard Changes"
        noText="Cancel"
        danger
        onClose={() => setShowConfirmExit(false)}
        onYes={() => discardChanges()}
        onNo={() => setShowConfirmExit(false)}
      />
    </div>
  );
}

interface Props {
  showModal: boolean;
  attributeName: string;
  units: string;
  isBoolean: boolean;
  isHighlightable: boolean;
  regEnumId: number | null;
  enumerationMap: EnumerationMap;
  highlightingRules: HighlightingRule[];
  onSave: (highlightingRules: HighlightingRule[]) => void;
  onClose: () => void;
}

interface HighlightingRule {
  comparator: string;
  value: string;
  valueType: string;
  colorHexCode: string;
  sortPriority: number;
}

interface FormRule {
  formId: number;
  comparator: string;
  valueType: string;
  value: string;
  colorHexCode: string;
}

interface EnumerationMap {
  [key: string]: EnumerationIndexMap;
}

interface EnumerationIndexMap {
  [key: string]: string;
}

interface EnumerationOption {
  index: number;
  name: string;
}

type ViewValueType = "NUMBER" | "MEDIAN" | "MEAN" | "STANDARD_DEVIATION" | "MAX" | "MIN";

type ViewComparator = "=" | "!=" | "<" | ">" | "<>";
